"use client"

/**
 * Shared 12-month "year at a glance" bar grid used by the seasonal-window
 * tools (aerate, mow, fertilizer, herbicide, soil-temp, seeding). Each tool
 * resolves its own per-month fill color and intensity; this primitive owns
 * the layout, today/best rings, today-dot adornment, and label styling so
 * the visual treatment stays consistent across tools.
 */

import type { CSSProperties } from "react"

export type { YearCalendarCell } from "@/lib/year-calendar-types"
import type { YearCalendarCell } from "@/lib/year-calendar-types"

export interface YearCalendarProps {
  /** Length 12; index 0 = January. */
  months: YearCalendarCell[]
  /** 0-indexed; defaults to `new Date().getMonth()`. Pass `-1` to disable. */
  thisMonth?: number
  /** 0-indexed best-window month. Wins over today ring + label color when both apply. */
  bestMonth?: number
  variant?: "compact" | "full"
  ringColors?: { today?: string; best?: string }
  /** Render a 6px dot above the current month bar (aerate / fert style). */
  todayDot?: boolean
  ariaLabel?: string
  className?: string
  style?: CSSProperties
}

const INK = "#111827"
const MUTED = "#6b7280"

export function YearCalendar({
  months,
  thisMonth,
  bestMonth,
  variant = "full",
  ringColors,
  todayDot = false,
  ariaLabel,
  className,
  style,
}: YearCalendarProps) {
  const currentMonth = thisMonth ?? new Date().getMonth()
  const isCompact = variant === "compact"
  const barHeight = isCompact ? 28 : 40
  const barRadius = isCompact ? 3 : 4
  const gridGap = isCompact ? 2 : 3
  const cellGap = isCompact ? 3 : 5
  const labelSize = isCompact ? 9 : 10
  const dotSize = 6
  const todayRing = ringColors?.today
  const bestRing = ringColors?.best

  return (
    <div
      className={className}
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(12, 1fr)",
        gap: gridGap,
        ...style,
      }}
      role="group"
      aria-label={ariaLabel}
    >
      {months.map((m, idx) => {
        const i = m.i ?? idx
        const isThis = i === currentMonth
        const isBest = bestMonth !== undefined && i === bestMonth
        const ringColor = isBest && bestRing
          ? bestRing
          : isThis && todayRing
            ? todayRing
            : null
        const barStyle: CSSProperties = {
          width: "100%",
          height: barHeight,
          borderRadius: barRadius,
          background: m.fill,
          position: "relative",
        }
        if (ringColor) {
          barStyle.boxShadow = `inset 0 0 0 2px ${ringColor}`
        }
        const labelColor = isBest && bestRing
          ? bestRing
          : isThis
            ? INK
            : MUTED
        return (
          <div
            key={i}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: cellGap,
            }}
          >
            <div style={barStyle}>
              {todayDot && isThis && (
                <div
                  style={{
                    position: "absolute",
                    top: -6,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: dotSize,
                    height: dotSize,
                    borderRadius: dotSize / 2,
                    background: INK,
                  }}
                />
              )}
            </div>
            <div
              style={{
                fontSize: labelSize,
                fontWeight: isThis || isBest ? 700 : 500,
                color: labelColor,
                letterSpacing: isCompact ? undefined : "0.02em",
              }}
            >
              {isCompact ? m.label.charAt(0) : m.label}
            </div>
          </div>
        )
      })}
    </div>
  )
}
